import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, Loader2, Search, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { api, formatMXN } from '@/lib/api';
import { productCatalogId, productPath } from '@/lib/catalog';
import { totalConIva } from '@/lib/precios';
import { ProductImage } from '@/components/catalog/ProductImage';
import type { Producto } from '@/types';
import { cn } from '@/lib/utils';

const RECENT_KEY = 'alarcom-search-recent';
const MAX_RECENT = 6;
const DEBOUNCE_MS = 280;
const RESULT_LIMIT = 8;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === 'string' && x.trim() !== '').slice(0, MAX_RECENT)
      : [];
  } catch {
    return [];
  }
}

function saveRecent(term: string) {
  const t = term.trim();
  if (t.length < 2) return;
  const next = [t, ...loadRecent().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(
    0,
    MAX_RECENT,
  );
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
}

function highlightMatch(text: string, query: string) {
  const q = query.trim();
  if (!q || !text) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded-sm bg-brand-100 px-0.5 font-semibold text-brand-800">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function ResultRow({
  product,
  query,
  active,
  onSelect,
}: {
  product: Producto;
  query: string;
  active: boolean;
  onSelect: () => void;
}) {
  const precio = product.precio_oferta ?? product.precio_venta;
  const modelo = product.modelo_pto || product.sku || '';

  return (
    <Link
      to={productPath(product)}
      onClick={onSelect}
      className={cn(
        'flex gap-3 px-3 py-2.5 transition-colors',
        active ? 'bg-brand-50' : 'hover:bg-surface-50',
      )}
      role="option"
      aria-selected={active}
      id={`search-opt-${productCatalogId(product)}`}
    >
      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-surface-200 bg-white">
        <ProductImage
          imagen={product.imagen}
          alt={product.nombre_pto}
          modelo={product.modelo_pto}
          categoria={product.categoria}
          fallbackLabel={product.nombre_pto}
          className="h-full w-full"
          priority
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-semibold leading-snug text-surface-900">
          {highlightMatch(product.nombre_pto, query)}
        </p>
        {modelo ? (
          <p className="mt-0.5 truncate font-mono text-[11px] text-surface-500">
            {highlightMatch(modelo, query)}
          </p>
        ) : null}
        <p className="mt-1 text-sm font-semibold tabular-nums text-brand-700">
          {formatMXN(totalConIva(precio))}
          <span className="ml-1 text-[11px] font-normal text-surface-500">c/IVA</span>
        </p>
      </div>
    </Link>
  );
}

function ResultsPanel({
  query,
  loading,
  items,
  total,
  activeIndex,
  recent,
  onPickRecent,
  onClearRecent,
  onSelectProduct,
  onSeeAll,
  listId,
}: {
  query: string;
  loading: boolean;
  items: Producto[];
  total: number;
  activeIndex: number;
  recent: string[];
  onPickRecent: (term: string) => void;
  onClearRecent: () => void;
  onSelectProduct: () => void;
  onSeeAll: () => void;
  listId: string;
}) {
  const q = query.trim();
  const showRecent = q.length < 2 && recent.length > 0;

  if (showRecent) {
    return (
      <div className="py-2">
        <div className="flex items-center justify-between px-3 pb-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">
            Recientes
          </p>
          <button
            type="button"
            onClick={onClearRecent}
            className="text-[11px] font-medium text-surface-500 hover:text-surface-800"
          >
            Limpiar
          </button>
        </div>
        {recent.map((term) => (
          <button
            key={term}
            type="button"
            onClick={() => onPickRecent(term)}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-surface-700 hover:bg-surface-50"
          >
            <Clock className="h-3.5 w-3.5 shrink-0 text-surface-400" />
            <span className="truncate">{term}</span>
          </button>
        ))}
      </div>
    );
  }

  if (q.length < 2) {
    return (
      <div className="px-4 py-6 text-center">
        <Search className="mx-auto h-8 w-8 text-surface-300" strokeWidth={1.5} />
        <p className="mt-2 text-sm font-medium text-surface-700">Busca equipo por modelo o nombre</p>
        <p className="mt-1 text-xs text-surface-500">Ej. cámara IP, Hikvision, NVR…</p>
      </div>
    );
  }

  if (loading && items.length === 0) {
    return (
      <div className="space-y-2 px-3 py-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex gap-3 animate-pulse">
            <div className="h-14 w-14 rounded-lg bg-surface-100" />
            <div className="flex-1 space-y-2 py-1">
              <div className="h-3 w-[80%] rounded bg-surface-100" />
              <div className="h-2.5 w-1/3 rounded bg-surface-100" />
              <div className="h-3 w-1/4 rounded bg-surface-100" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm font-semibold text-surface-800">Sin resultados para “{q}”</p>
        <p className="mt-1 text-xs text-surface-500">Prueba otro modelo, marca o SKU.</p>
        <button type="button" onClick={onSeeAll} className="btn-secondary mt-4 text-xs">
          Ir al catálogo
        </button>
      </div>
    );
  }

  return (
    <div>
      <div
        id={listId}
        role="listbox"
        aria-label="Resultados de búsqueda"
        className="max-h-[min(60vh,420px)] overflow-y-auto overscroll-contain py-1"
      >
        {items.map((p, i) => (
          <ResultRow
            key={productCatalogId(p)}
            product={p}
            query={q}
            active={i === activeIndex}
            onSelect={onSelectProduct}
          />
        ))}
      </div>
      <div className="border-t border-surface-100 p-2">
        <button
          type="button"
          onClick={onSeeAll}
          className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50"
        >
          <span>Ver todos{total > items.length ? ` (${total})` : ''}</span>
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function HeaderSearch() {
  const navigate = useNavigate();
  const listId = useId();
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const desktopRootRef = useRef<HTMLDivElement>(null);
  const reqIdRef = useRef(0);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Producto[]>([]);
  const [total, setTotal] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recent, setRecent] = useState<string[]>(() => loadRecent());

  const closeAll = useCallback(() => {
    setOpen(false);
    setSheetOpen(false);
    setActiveIndex(-1);
  }, []);

  const runSearch = useCallback(async (term: string) => {
    const q = term.trim();
    if (q.length < 2) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    const reqId = ++reqIdRef.current;
    setLoading(true);

    try {
      const res = await api.productos({ q, limit: RESULT_LIMIT, page: 1 });
      if (reqId !== reqIdRef.current) return;
      setItems(res.items ?? []);
      setTotal(res.total ?? res.items?.length ?? 0);
      setActiveIndex(res.items?.length ? 0 : -1);
    } catch {
      if (reqId !== reqIdRef.current) return;
      setItems([]);
      setTotal(0);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void runSearch(query);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, runSearch]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!desktopRootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open && !sheetOpen) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') closeAll();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, sheetOpen, closeAll]);

  useEffect(() => {
    if (sheetOpen) {
      document.body.style.overflow = 'hidden';
      window.setTimeout(() => mobileInputRef.current?.focus(), 50);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sheetOpen]);

  const goCatalog = (term?: string) => {
    const q = (term ?? query).trim();
    if (q) saveRecent(q);
    setRecent(loadRecent());
    closeAll();
    navigate(q ? `/catalogo?q=${encodeURIComponent(q)}` : '/catalogo');
  };

  const onSelectProduct = () => {
    if (query.trim()) {
      saveRecent(query.trim());
      setRecent(loadRecent());
    }
    closeAll();
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (activeIndex >= 0 && items[activeIndex]) {
      onSelectProduct();
      navigate(productPath(items[activeIndex]));
      return;
    }
    goCatalog();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!items.length) return;
      setActiveIndex((i) => (i + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!items.length) return;
      setActiveIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0 && items[activeIndex]) {
      e.preventDefault();
      onSelectProduct();
      navigate(productPath(items[activeIndex]));
    }
  };

  const panelProps = {
    query,
    loading,
    items,
    total,
    activeIndex,
    recent,
    onPickRecent: (term: string) => {
      setQuery(term);
      setOpen(true);
      void runSearch(term);
    },
    onClearRecent: () => {
      localStorage.removeItem(RECENT_KEY);
      setRecent([]);
    },
    onSelectProduct,
    onSeeAll: () => goCatalog(),
    listId,
  };

  return (
    <>
      {/* Desktop — busca en vivo bajo el input */}
      <div ref={desktopRootRef} className="relative hidden max-w-md flex-1 lg:block">
        <form onSubmit={onSubmit} className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
          <input
            ref={desktopInputRef}
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder="Buscar modelo, nombre o SKU…"
            className="input-field py-2 pl-9 pr-9"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              activeIndex >= 0 && items[activeIndex]
                ? `search-opt-${productCatalogId(items[activeIndex])}`
                : undefined
            }
            autoComplete="off"
          />
          {loading ? (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-brand-600" />
          ) : query ? (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-surface-400 hover:bg-surface-100 hover:text-surface-700"
              aria-label="Limpiar"
              onClick={() => {
                setQuery('');
                setItems([]);
                desktopInputRef.current?.focus();
              }}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </form>

        {open && (
          <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-[60] overflow-hidden rounded-xl border border-surface-200 bg-white shadow-card-hover animate-fade-in">
            <ResultsPanel {...panelProps} />
          </div>
        )}
      </div>

      {/* Móvil — icono abre hoja */}
      <button
        type="button"
        className="icon-btn lg:hidden"
        aria-label="Buscar"
        onClick={() => setSheetOpen(true)}
      >
        <Search className="h-4 w-4" />
      </button>

      {sheetOpen && (
        <div
          className="fixed inset-0 z-[70] lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Buscar productos"
        >
          <button
            type="button"
            className="absolute inset-0 bg-surface-950/40 backdrop-blur-[2px]"
            aria-label="Cerrar búsqueda"
            onClick={closeAll}
          />
          <div className="absolute inset-x-0 top-0 flex max-h-[92dvh] flex-col rounded-b-2xl border-b border-surface-200 bg-white shadow-drawer animate-fade-in">
            <div className="flex items-center gap-2 border-b border-surface-100 px-3 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
              <form onSubmit={onSubmit} className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
                <input
                  ref={mobileInputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Modelo, nombre o SKU…"
                  className="input-field-lg pl-9 pr-9"
                  role="combobox"
                  aria-expanded
                  aria-controls={listId}
                  autoComplete="off"
                />
                {loading && (
                  <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-brand-600" />
                )}
              </form>
              <button type="button" className="btn-ghost shrink-0 px-2" onClick={closeAll}>
                Cerrar
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <ResultsPanel {...panelProps} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
